/**
 * The floor's arithmetic, on the page.
 *
 * A response that was filtered by the trust floor carries a `trust_floor`
 * block — `{floor, total, clears, below, unrated}` — and two places on a Feed
 * or Find page have to say what is in it: the stat strip above the grid, and
 * the status line beside the section heading.
 *
 * Neither surface reports what the floor hid any more -- not `unrated`, and no
 * longer `below` either. Both are counts of listings the visitor cannot see,
 * and a page that opens with them is telling a buyer how much of the catalog it
 * is withholding in figures they can do nothing with. What the floor does is
 * said in prose they can act on instead: the hero states the thresholds and the
 * top bar's selector changes them.
 *
 * The distinction between the two is still real and still enforced everywhere
 * they *are* reported -- "this seller is not good enough" and "we hold no
 * numbers for this seller" are different sentences, only the second is our own
 * fault, and they must never be summed into one "hidden" total. The one surface
 * that still reports them is the floored-empty state in console-states.js,
 * where explaining an empty grid is the entire job. The `trust_floor` block
 * still carries both figures; this module draws neither.
 *
 * Every function here is a no-op on a page with none of the markup, so callers
 * do not have to know which page they are on.
 */

import { FLOOR_NAMES } from './floor-state.js';

/** 1284 -> "1,284". The strip is tabular-nums, so the separators line up. */
function num(value) {
  return Number(value || 0).toLocaleString();
}

function plural(count, word) {
  return `${num(count)} ${word}${Number(count) === 1 ? '' : 's'}`;
}

/**
 * Repaint the stat strip from a `trust_floor` block.
 *
 * The Feed's strip no longer draws `below` or `unrated`, but this still handles
 * them: the markup is per page, and a page that renders one of those cards has
 * to hide it when the floor is off. With no floor applied they are structurally
 * zero rather than measured — nothing was tested, so nothing failed — and a
 * card reading "0 below the floor" would imply a check that never ran. The
 * strip's `auto-fit` columns close the gap.
 */
export function paintFloorStats(counts) {
  const stats = document.querySelectorAll('[data-floor-stat]');
  if (!stats.length) return;

  const off = !counts || counts.floor === 'off' || !counts.floor;
  const name = counts && FLOOR_NAMES[counts.floor];

  for (const stat of stats) {
    const key = stat.dataset.floorStat;
    const measured = !off || key === 'clears' || key === 'total';
    stat.hidden = !measured;
    if (!measured) continue;

    const label = stat.querySelector('[data-floor-stat-label]');
    const value = stat.querySelector('[data-floor-stat-value]');
    if (label && key === 'clears') {
      // With no floor applied the same number is not "clearing" anything.
      label.textContent = off ? 'Listings tracked' : `Clearing ${name} now`;
    }
    if (value && counts && key in counts) value.textContent = num(counts[key]);
  }
}

/**
 * Repaint the one-line summary under a section heading.
 *
 * `shown` is what the grid actually drew, which is not `counts.clears`: the
 * feed asks for a page of listings, not all of them, and the cap hides some of
 * what it drew behind "Show more".
 */
export function paintFeedStatus(shown, counts) {
  const nodes = document.querySelectorAll('[data-feed-status]');
  if (!nodes.length) return;

  let text;
  if (!counts || !counts.floor || counts.floor === 'off') {
    text = `Showing ${plural(shown, 'listing')} · every seller`;
  } else {
    const name = FLOOR_NAMES[counts.floor] || counts.floor;
    // "Showing 0 listings · Strict sellers" is a sentence about nothing.
    // When the floor cleared no one, say that -- the grid's own empty state
    // says the same thing at length.
    const hidden = (counts.below || 0) + (counts.unrated || 0);
    text = counts.clears
      ? `Showing ${plural(shown, 'listing')} · ${name} sellers, returns accepted`
        + (hidden ? ` · ${num(hidden)} hidden` : '')
      : `Nothing clears ${name} right now`;
    // The hidden count is back, at the end of the line where it reads as the
    // cost of the standard rather than the headline: the standard is the
    // visitor's choice, and the number is what they are trading for it.
  }
  for (const node of nodes) node.textContent = text;
}

/**
 * Counts for a narrower set than the last fetch described.
 *
 * `apiService.lastTrustFloor` describes whatever the last product request
 * asked for, and on /search.html that is the whole purchasable catalog: the
 * page loads every listing once and filters by keyword in the browser, so the
 * request carries no `search` at all. Reading the block straight off it made
 * the page state two different totals for one query -- "0 of 12 listings" over
 * a grid drawing nothing, beside "23 hidden at Strict" describing listings
 * that had nothing to do with the search. Worse, the empty state picked the
 * wrong branch off it: with `clears` non-zero it concluded the floor was not
 * the problem and said "Nothing matched stand mixer" on a query the floor may
 * well have emptied.
 *
 * So the search page fetches the same block scoped to its own query -- the API
 * honours `search` on both the rows and the counts -- and parks it here. Null
 * means "nothing narrower than the last fetch", which is every other page.
 */
let scopedCounts = null;

export function setScopedFloorCounts(counts) {
  scopedCounts = counts || null;
}

export function getFloorCounts() {
  if (scopedCounts) return scopedCounts;
  return (typeof window !== 'undefined'
    && window.apiService
    && window.apiService.lastTrustFloor) || null;
}

/** Both of the above, from whatever describes what the grid just drew. */
export function paintFeedStats(shown) {
  const counts = getFloorCounts();
  paintFloorStats(counts);
  paintFeedStatus(shown, counts);
}
